declare global {
  interface Window {
    SigmaDeviceManager?: {
      getSessionToken(): Promise<string>;
    };
    digitalIntelligencePlugin?: {
      getSessionToken: () => Promise<string>;
      destroy: () => void;
    };
  }
}
const SESSION_STORAGE_KEY = 'effectiv_session_token';

const setToken = (value: string) => {
  // eslint-disable-next-line no-console
  console.info('Digital Intelligence: setToken called');
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, value);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Digital Intelligence: sessionStorage write failed', e);
  }
  try {
    localStorage.setItem(SESSION_STORAGE_KEY, value);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Digital Intelligence: localStorage write failed', e);
  }
};

export class DigitalIntelligencePlugin {
  private static instance: DigitalIntelligencePlugin | null = null;

  constructor(sdkKey?: string) {
    if (DigitalIntelligencePlugin.instance) {
      return DigitalIntelligencePlugin.instance;
    }

    if (navigator?.userAgent.indexOf('reactNativeWebview') === -1) {
      this.init(sdkKey);
    } else {
      setToken(
        'FAILED TO OBTAIN FINGERPRINT: React Native Webview detected, SDK not initialized'
      );
    }

    DigitalIntelligencePlugin.instance = this;
    this.setupGlobalInterface();
  }

  private setupGlobalInterface() {
    window.digitalIntelligencePlugin = {
      getSessionToken: this.getSessionToken.bind(this),
      destroy: DigitalIntelligencePlugin.destroy.bind(this)
    };
  }

  private init(sdkKey?: string) {
    // eslint-disable-next-line no-console
    console.info('Digital Intelligence: initSDK called');
    if (!sdkKey) {
      setToken('FAILED TO OBTAIN FINGERPRINT: SDK Key is required');
      return;
    }

    // Try to get token from existing SDK first
    try {
      window.SigmaDeviceManager?.getSessionToken()
        ?.then((sessionToken: string) => {
          if (sessionToken) {
            setToken(sessionToken);
            return;
          }
        })
        .catch(() => {
          // SDK not initialized, continue to initialize
        });
    } catch (error) {
      // Continue to initialize
    }

    (function (window, document) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.defer = true;
      script.id = 'socure-sdk-script';
      script.src = 'https://sdk.dv.socure.io/latest/device-risk-sdk.js';
      script.setAttribute('data-public-key', sdkKey);

      script.onload = function () {
        try {
          window.SigmaDeviceManager?.getSessionToken()
            .then((sessionToken: string) => {
              if (sessionToken) {
                setToken(sessionToken);
              } else {
                throw new Error('Empty session token received');
              }
            })
            .catch((error: Error) => {
              setToken(`FAILED TO OBTAIN FINGERPRINT: ${error.message}`);
            });
        } catch (error) {
          setToken(
            `FAILED TO OBTAIN FINGERPRINT: ${
              error instanceof Error ? error.message : 'Unknown error'
            }`
          );
        }
      };

      script.onerror = function () {
        setToken(
          'FAILED TO OBTAIN FINGERPRINT: Failed to load Socure SDK script'
        );
      };

      const firstScript = document.getElementsByTagName('script')[0];
      if (firstScript?.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      } else {
        document.head.appendChild(script);
      }
    })(window, document);
  }

  private async getSessionToken(): Promise<string> {
    try {
      const sessionToken = await window.SigmaDeviceManager?.getSessionToken();
      if (sessionToken) {
        setToken(sessionToken);
        return sessionToken;
      }
      throw new Error('Failed to get session token');
    } catch (error) {
      const errorMessage = `FAILED TO OBTAIN FINGERPRINT: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`;
      setToken(errorMessage);
      throw error;
    }
  }

  static destroy(): void {
    const script = document.getElementById('socure-sdk-script');
    if (script?.parentNode) {
      script.parentNode.removeChild(script);
    }
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    localStorage.removeItem(SESSION_STORAGE_KEY);
    DigitalIntelligencePlugin.instance = null;
    delete window.digitalIntelligencePlugin;
  }
}
